import { createContext, useContext, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useLazyGetFixedPricingStatesQuery } from "@/redux/apis/config/trip/tripCost";
import { useAppSelector } from "@/redux/hooks";
import { fixedPricingStateSelector, fixedPricingStatesSelector } from "@/redux/slices/trip/selectors";
import { FixedPricingState } from "@/redux/slices/trip/types";
import { usePricingProfileContext } from "../PricingProfile/PricingProfileContext";

interface StateRatesState {
  states: FixedPricingState[];
  activeState?: FixedPricingState;
  stateName: string | null;
  isFetching: boolean;
  isRestricted: boolean;
}

const StateRatesContext = createContext<StateRatesState | null>(null);

export const StateRatesContextProvider = ({ children }: React.PropsWithChildren) => {
  const { isFetching: isFetchingPricingProfiles, pricingProfile } = usePricingProfileContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const stateName = searchParams.get("stateName");
  const { slug = "" } = useParams();

  const [getStates, { isFetching: isFetchingStates, isSuccess }] = useLazyGetFixedPricingStatesQuery();
  const states = useAppSelector(fixedPricingStatesSelector);
  const activeState = useAppSelector(fixedPricingStateSelector(slug));
  const [isRestricted, setIsRestricted] = useState(false);

  useEffect(() => {
    if (!isFetchingPricingProfiles && pricingProfile) getStates({ id: pricingProfile.id });
  }, [isFetchingPricingProfiles, pricingProfile, getStates]);

  // breadcrumbs setup (no new history entries)
  useEffect(() => {
    if (!activeState || activeState.managed) return;

    const nextName = activeState.name;
    if (stateName !== nextName) {
      const next = new URLSearchParams(searchParams);
      next.set("stateName", nextName);
      setSearchParams(next, { replace: true });
    }
    // Only depend on values we compare/use
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeState?.name, activeState?.managed, stateName, setSearchParams]);

  // restricted check
  useEffect(() => {
    if (isSuccess && (!activeState || activeState.managed)) setIsRestricted(true);
    else setIsRestricted(false);
  }, [isSuccess, activeState]);

  return (
    <StateRatesContext.Provider value={{ states, activeState, stateName, isFetching: isFetchingStates, isRestricted }}>
      {children}
    </StateRatesContext.Provider>
  );
};

export const useStateRatesContext = () => {
  const context = useContext(StateRatesContext);
  if (!context) throw new Error("useStateRatesContext must be used within a StateRatesContextProvider");

  return context;
};
